import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronRightIcon, FolderIcon } from '@heroicons/react/24/outline';
import { useSidebar } from './SidebarContext';

export interface SidebarProjectNode {
  id: number;
  slug: string; // Phase 27 D-15.4: slug for sidebar chip display
  name: string;
  parentId: number | null;
  state: string;
  isMember: boolean;
}

interface SidebarProjectTreeProps {
  data: SidebarProjectNode[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  routePrefix: '/projects' | '/external/projects';
  /** sessionStorage key for per-node expansion state. Per-audience keys to avoid cross-talk. */
  expandedKey: string;
  /** Audience-specific empty-state copy (already escaped JSX-safe — caller passes &apos; if needed). */
  emptyCopy: string;
}

function readExpanded(key: string): Record<number, boolean> {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    const out: Record<number, boolean> = {};
    for (const [k, v] of Object.entries(parsed)) {
      const id = Number(k);
      if (Number.isFinite(id) && typeof v === 'boolean') out[id] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function writeExpanded(key: string, value: Record<number, boolean>): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / unavailable storage */
  }
}

export function SidebarProjectTree(props: SidebarProjectTreeProps) {
  const { data, isLoading, isError, onRetry, routePrefix, expandedKey, emptyCopy } = props;
  const { isCollapsed } = useSidebar();
  const [expanded, setExpanded] = useState<Record<number, boolean>>(() =>
    readExpanded(expandedKey),
  );

  useEffect(() => {
    writeExpanded(expandedKey, expanded);
  }, [expanded, expandedKey]);

  // Build childMap: parentId → children list (stable sort by name).
  const { roots, childMap } = useMemo(() => {
    const list = data ?? [];
    const ids = new Set(list.map((n) => n.id));
    const cm = new Map<number, SidebarProjectNode[]>();
    const rs: SidebarProjectNode[] = [];
    for (const n of list) {
      if (n.parentId === null || !ids.has(n.parentId)) {
        rs.push(n);
      } else {
        const arr = cm.get(n.parentId) ?? [];
        arr.push(n);
        cm.set(n.parentId, arr);
      }
    }
    const byName = (a: SidebarProjectNode, b: SidebarProjectNode) => a.name.localeCompare(b.name);
    rs.sort(byName);
    for (const arr of cm.values()) arr.sort(byName);
    return { roots: rs, childMap: cm };
  }, [data]);

  if (isCollapsed) return null;

  if (isLoading) {
    return (
      <>
        <div className="h-8 mx-3 my-1 rounded-lg bg-gray-100 animate-pulse" />
        <div className="h-8 mx-3 my-1 rounded-lg bg-gray-100 animate-pulse" />
        <div className="h-8 mx-3 my-1 rounded-lg bg-gray-100 animate-pulse" />
      </>
    );
  }

  if (isError) {
    return (
      <div className="px-3 py-2 text-sm font-medium text-gray-500">
        Couldn&apos;t load projects.{' '}
        <button
          type="button"
          onClick={onRetry}
          className="text-sm font-medium text-primary-600 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <div className="px-3 py-2 text-sm font-medium text-gray-500">{emptyCopy}</div>;
  }

  function isOpen(id: number): boolean {
    return expanded[id] === undefined ? true : expanded[id];
  }

  function toggle(id: number, ev: React.MouseEvent): void {
    ev.stopPropagation();
    ev.preventDefault();
    setExpanded((prev) => ({ ...prev, [id]: !isOpen(id) }));
  }

  function renderNode(node: SidebarProjectNode): React.ReactElement {
    const children = childMap.get(node.id) ?? [];
    const hasChildren = children.length > 0;
    const open = isOpen(node.id);

    const labelMember = (
      <NavLink
        to={`${routePrefix}/${node.id}/board`}
        className={({ isActive }: { isActive: boolean }) =>
          `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors flex-1 min-w-0 ${
            isActive
              ? 'bg-primary-50 text-primary-700'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`
        }
      >
        <FolderIcon className="w-5 h-5 shrink-0" />
        <span className="truncate">{node.name}</span>
      </NavLink>
    );

    const labelGhost = (
      <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-400 cursor-default flex-1 min-w-0">
        <FolderIcon className="w-5 h-5 shrink-0 text-gray-400" />
        <span className="truncate">{node.name}</span>
      </div>
    );

    return (
      <div key={node.id}>
        <div className="flex items-center">
          {hasChildren ? (
            <button
              type="button"
              onClick={(ev) => toggle(node.id, ev)}
              className="flex h-8 w-6 items-center justify-center text-gray-500 hover:text-gray-900"
            >
              <ChevronRightIcon
                className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`}
              />
            </button>
          ) : (
            <span className="w-6 shrink-0" aria-hidden />
          )}
          {node.isMember ? labelMember : labelGhost}
        </div>
        {hasChildren && open && (
          <div className="ml-4">{children.map(renderNode)}</div>
        )}
      </div>
    );
  }

  return <>{roots.map(renderNode)}</>;
}
